import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Loader2 } from 'lucide-react';
import { useQuery } from '@apollo/client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DetailField, DetailFieldsGrid, DetailSection } from '@/components/ui/details-panel';
import type { ChargeLedgerEntryItem } from './types';
import { formatCurrency, formatDateTime } from './utils';
import { PARKING_SESSION_CHARGE_LEDGER_QUERY } from './parking-sessions.gql';

interface ParkingSessionLedgerProps {
	sessionId: string;
	isActive: boolean;
}

const calculateTotals = (entries: ChargeLedgerEntryItem[]) => {
	return entries.reduce(
		(acc, entry) => {
			const amount = entry.amount ?? 0;
			const signedAmount = entry.isDebit ? amount : -amount;

			acc.totalDebits += entry.isDebit ? amount : 0;
			acc.totalCredits += entry.isDebit ? 0 : amount;
			acc.netTotal += signedAmount;
			acc.totalComputed += entry.computedCharge ?? 0;
			acc.totalAfterLimits += entry.chargeAfterLimits ?? 0;
			acc.totalFinal += amount;
			return acc;
		},
		{
			totalDebits: 0,
			totalCredits: 0,
			netTotal: 0,
			totalComputed: 0,
			totalAfterLimits: 0,
			totalFinal: 0
		}
	);
};

interface LedgerEntryProps {
	entry: ChargeLedgerEntryItem;
	t: (key: string, options?: Record<string, unknown>) => string;
}

const LedgerEntry = ({ entry, t }: LedgerEntryProps) => {
	const [expanded, setExpanded] = useState(false);
	const amount = entry.amount ?? 0;
	const signedAmount = entry.isDebit ? amount : -amount;

	return (
		<div className="pb-4 border-b border-gray-100 last:border-b-0 last:pb-0 dark:border-slate-800">
			<div className="flex items-start justify-between mb-3">
				<div className="flex flex-col gap-1">
					<div className="flex flex-wrap items-center gap-2">
						<Badge variant="outline" className="text-xs font-medium">
							{entry.transactionTypeName}
						</Badge>
						{entry.transactionTypeCode && (
							<span className="text-xs text-muted-foreground">
								{entry.transactionTypeCode}
							</span>
						)}
						{entry.isReversed && (
							<Badge variant="destructive" className="text-[10px]">
								{t('ledger.reversed')}
							</Badge>
						)}
					</div>
					<span className="text-sm text-foreground">
						{formatDateTime(entry.transactionDateTime)}
					</span>
					{entry.description && (
						<span className="text-xs text-muted-foreground">
							{entry.description}
						</span>
					)}
				</div>
				<div className="text-right">
					<p className="text-base font-semibold">
						{formatCurrency(signedAmount)}
					</p>
					{!entry.isDebit && (
						<p className="text-[11px] text-muted-foreground">
							{t('ledger.creditEntry')}
						</p>
					)}
				</div>
			</div>

			<DetailFieldsGrid>
				{entry.paymentMethodName && (
					<DetailField
						label={t('ledger.paymentMethod')}
						value={entry.paymentMethodName}
					/>
				)}
				{entry.computedCharge != null && (
					<DetailField
						label={t('ledger.computedCharge')}
						value={formatCurrency(entry.computedCharge)}
					/>
				)}
				{entry.chargeAfterLimits != null && (
					<DetailField
						label={t('ledger.chargeAfterLimits')}
						value={formatCurrency(entry.chargeAfterLimits)}
					/>
				)}
			</DetailFieldsGrid>

			{entry.lines.length > 0 && (
				<div className="mt-3">
					<Button
						variant="ghost"
						size="sm"
						className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
						onClick={() => setExpanded(!expanded)}
					>
						<ChevronRight
							className={`w-3.5 h-3.5 mr-1 transition-transform duration-200 ${expanded ? 'rotate-45' : ''}`}
						/>
						{t('ledger.lines', { count: entry.lines.length })}
					</Button>

					{expanded && (
						<div className="mt-2 ml-4 space-y-2 border-l-2 border-muted pl-3">
							{entry.lines.map((line) => (
								<div
									key={line.chargeLedgerEntryLineId}
									className="p-2 rounded-md bg-muted/50"
								>
									<div className="flex items-center justify-between text-xs font-semibold">
										<span>
											{t('ledger.lineLabel')} {line.lineSequence ?? '—'}
										</span>
										<span>{formatCurrency(line.lineCharge ?? 0)}</span>
									</div>
									<div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
										{line.lineStartDateTime && (
											<p>
												{formatDateTime(line.lineStartDateTime)}
												{line.lineEndDateTime
													? ` → ${formatDateTime(line.lineEndDateTime)}`
													: ''}
											</p>
										)}
										{line.description && <p>{line.description}</p>}
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

interface ChargeLedgerQueryResponse {
	parkingSession?: {
		parkingSessionId: string;
		chargeLedgerEntries?: Array<{
			chargeLedgerEntryId: string;
			transactionDateTime: string;
			computedCharge?: number | null;
			chargeAfterLimits?: number | null;
			amount?: number | null;
			description?: string | null;
			isReversed: boolean;
			relatedEntityType?: string | null;
			relatedEntityId?: string | null;
			transactionType: {
				transactionTypeId?: number | null;
				name?: string | null;
				code?: string | null;
				isDebit: boolean;
			};
			paymentMethod?: {
				paymentMethodId: number;
				name?: string | null;
			} | null;
			lines?: Array<{
				chargeLedgerEntryLineId: string;
				lineSequence?: number | null;
				lineStartDateTime?: string | null;
				lineEndDateTime?: string | null;
				lineCharge?: number | null;
				description?: string | null;
				calculatedAt: string;
			}> | null;
		}> | null;
	} | null;
}

const mapChargeLedgerEntries = (
	rawEntries: NonNullable<NonNullable<ChargeLedgerQueryResponse['parkingSession']>['chargeLedgerEntries']>
): ChargeLedgerEntryItem[] => {
	return rawEntries.map((entry) => ({
		chargeLedgerEntryId: entry.chargeLedgerEntryId,
		transactionDateTime: entry.transactionDateTime,
		computedCharge: entry.computedCharge ?? undefined,
		chargeAfterLimits: entry.chargeAfterLimits ?? undefined,
		amount: entry.amount ?? undefined,
		description: entry.description ?? undefined,
		isReversed: entry.isReversed,
		transactionTypeName: entry.transactionType?.name ?? 'Unknown',
		transactionTypeCode: entry.transactionType?.code ?? undefined,
		isDebit: entry.transactionType?.isDebit ?? true,
		paymentMethodName: entry.paymentMethod?.name ?? undefined,
		relatedEntityType: entry.relatedEntityType ?? undefined,
		relatedEntityId: entry.relatedEntityId ?? undefined,
		lines:
			entry.lines?.map((line) => ({
				chargeLedgerEntryLineId: line.chargeLedgerEntryLineId,
				lineSequence: line.lineSequence ?? undefined,
				lineStartDateTime: line.lineStartDateTime ?? undefined,
				lineEndDateTime: line.lineEndDateTime ?? undefined,
				lineCharge: line.lineCharge ?? undefined,
				description: line.description ?? undefined,
				calculatedAt: line.calculatedAt
			})) ?? []
	}));
};

export const ParkingSessionLedger = ({ sessionId, isActive }: ParkingSessionLedgerProps) => {
	const { t } = useTranslation('parkingSessions');

	const { data, loading, error } = useQuery<ChargeLedgerQueryResponse>(
		PARKING_SESSION_CHARGE_LEDGER_QUERY,
		{
			variables: { parkingSessionId: sessionId },
			skip: !isActive,
			fetchPolicy: 'cache-and-network'
		}
	);

	const entries = useMemo(() => {
		if (!data?.parkingSession?.chargeLedgerEntries) return [];
		return mapChargeLedgerEntries(data.parkingSession.chargeLedgerEntries);
	}, [data]);

	const sortedEntries = useMemo(
		() =>
			[...entries].sort(
				(a, b) =>
					new Date(b.transactionDateTime).getTime() -
					new Date(a.transactionDateTime).getTime()
			),
		[entries]
	);

	const totals = useMemo(() => calculateTotals(entries), [entries]);

	if (loading && entries.length === 0) {
		return (
			<div className="flex items-center justify-center p-8">
				<Loader2 className="w-5 h-5 mr-2 animate-spin text-muted-foreground" />
				<p className="text-sm text-muted-foreground">{t('ledger.loading')}</p>
			</div>
		);
	}

	if (error && entries.length === 0) {
		return (
			<div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
				<p className="text-sm text-destructive">{t('ledger.error')}</p>
			</div>
		);
	}

	if (entries.length === 0) {
		return (
			<div className="py-8 text-center text-muted-foreground">
				<p>{t('ledger.noEntries')}</p>
			</div>
		);
	}

	return (
		<div className="space-y-6 text-sm">
			<DetailSection title={t('ledger.summary')} noBorderTop>
				<DetailFieldsGrid>
					<DetailField
						label={t('ledger.totalCharges')}
						value={formatCurrency(totals.totalDebits)}
					/>
					<DetailField
						label={t('ledger.totalCredits')}
						value={formatCurrency(totals.totalCredits)}
					/>
					<DetailField
						label={t('ledger.netTotal')}
						value={<span className="text-lg font-semibold">{formatCurrency(totals.netTotal)}</span>}
					/>
				</DetailFieldsGrid>
			</DetailSection>

			<DetailSection title={`${t('ledger.entries')} (${sortedEntries.length})`}>
				<div className="space-y-4">
					{sortedEntries.map((entry) => (
						<LedgerEntry
							key={entry.chargeLedgerEntryId}
							entry={entry}
							t={t}
						/>
					))}
				</div>
			</DetailSection>
		</div>
	);
};

